/**
 * Shared shapes and helpers of the key components (contract keys section 0.2.0): pk-key-table, pk-key-detail,
 * pk-destroy-key-flow.
 */

/** A pending destroy request on a key. */
export interface PkPendingDestroy {
    requestId: string;
    requestedBy: string;
    requestedAtUtc?: string | null;
    expiresAtUtc?: string | null;
}

/** The evidence of a destroyed key. */
export interface PkTombstone {
    destroyedBy: string;
    approvedBy?: string | null;
    reason?: string | null;
    override: boolean;
}

/** One key (contract `Key` / `AdminKey`). */
export interface PkKeyRecord {
    id: string;
    kind: string;
    custody: string;
    state: string;
    thumbprint?: string | null;
    subject?: string | null;
    friendlyName?: string | null;
    hostname?: string | null;
    notAfter?: string | null;
    dataClass?: string | null;
    epoch?: number | null;
    createdAtUtc?: string | null;
    retiredAtUtc?: string | null;
    disabledAtUtc?: string | null;
    revokedAtUtc?: string | null;
    destroyedAtUtc?: string | null;
    hasEscrow?: boolean;
    wrapsKeyId?: string | null;
    pendingDestroy?: PkPendingDestroy | null;
    tombstone?: PkTombstone | null;
    tenantId?: string | null;
}

/** One key audit event (contract `KeyAuditEvent`). */
export interface PkKeyAuditRecord {
    atUtc: string;
    type: string;
    actor: string;
    subject: string;
    detail: string;
}

/** A key with its escrow params and audit tail (contract `KeyDetail`). */
export interface PkKeyDetailRecord extends PkKeyRecord {
    escrowParams?: Record<string, unknown> | null;
    audit?: PkKeyAuditRecord[];
}

/** What a key protects (contract `KeyImpact`). */
export interface PkKeyImpact {
    objectCount: number;
    epochs?: number[];
    dataClasses?: string[];
    oldestUtc?: string | null;
    newestUtc?: string | null;
    dependents?: string[];
}

/** `pk-status-pill` kind for a key state. */
export function stateKind(state: string): 'success' | 'warn' | 'danger' | 'info' | 'neutral' {
    switch (state) {
        case 'active': return 'success';
        case 'retired': return 'info';
        case 'disabled': return 'warn';
        case 'destroy-pending': return 'warn';
        case 'revoked': return 'danger';
        case 'destroyed': return 'danger';
        default: return 'neutral';
    }
}

/** Human label of a key state. */
export function stateLabel(state: string): string {
    switch (state) {
        case 'active': return 'Active';
        case 'retired': return 'Retired';
        case 'disabled': return 'Disabled';
        case 'revoked': return 'Revoked';
        case 'destroy-pending': return 'Destroy pending';
        case 'destroyed': return 'Destroyed';
        default: return state || 'Unknown';
    }
}

/** Human label of a custody model. */
export function custodyLabel(custody: string): string {
    switch (custody) {
        case 'customer': return 'Customer-held';
        case 'escrowed': return 'Escrowed';
        case 'provider': return 'Provider';
        case 'byok': return 'BYOK';
        default: return custody || 'Unknown';
    }
}

/** Human label of a key kind. */
export function kindLabel(kind: string): string {
    switch (kind) {
        case 'customer-cert': return 'Certificate';
        case 'tenant-kek': return 'Tenant key';
        case 'dek': return 'Data key';
        case 'platform-ring': return 'Platform ring';
        default: return kind || 'Unknown';
    }
}

/** The name to show for a key: friendly name, then subject, then thumbprint, then id. */
export function keyName(key: PkKeyRecord): string {
    return key.friendlyName || key.subject || key.thumbprint || key.id;
}

/** What a user types to confirm a destroy: the thumbprint when there is one, else the id (the API accepts either). */
export function confirmationText(key: PkKeyRecord): string {
    return key.thumbprint || key.id;
}

/** Whether `typed` confirms `key` (the id or the thumbprint, case-insensitive, trimmed — the API's rule). */
export function confirms(key: PkKeyRecord, typed: string): boolean {
    const t = typed.trim().toLowerCase();
    if (!t) return false;
    return t === key.id.toLowerCase() || (!!key.thumbprint && t === key.thumbprint.toLowerCase());
}

/** Replaces `{id}` and `{requestId}` in a URL template (URL-escaped). */
export function fillKeyUrl(template: string, id: string, requestId = ''): string {
    return template.replace('{id}', encodeURIComponent(id)).replace('{requestId}', encodeURIComponent(requestId));
}

/** Parses a JSON attribute into an object of type T, or null. */
export function parseJsonRecord<T>(raw: string | null): T | null {
    if (!raw) return null;
    try {
        const parsed: unknown = JSON.parse(raw);
        return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as T : null;
    } catch {
        return null;
    }
}

/** Case-insensitive email equality (actors are emails). */
export function sameActor(a: string | null | undefined, b: string | null | undefined): boolean {
    return !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();
}
